(function () {
    'use strict';

    // window.cwProxy(root)
    // Returns a Proxy of `document` that routes DOM-query/event APIs to `root`
    // (the shadow root). Inline user scripts in widget mode are PHP-wrapped as
    // (function(document){...}).call(this, window.cwProxy(this.getRootNode()))
    // so that calls like document.getElementById resolve against the shadow tree.
    if (typeof window.cwProxy !== 'function') {
        const rootMethods = [
            'getElementById',
            'querySelector', 'querySelectorAll',
            'getElementsByClassName', 'getElementsByTagName',
            'getElementsByName',
            'addEventListener', 'removeEventListener', 'dispatchEvent',
            'contains'
        ];

        const rootPropOverrides = {
            body: true, head: true, documentElement: true,
            activeElement: true, scrollingElement: true,
            firstElementChild: true, lastElementChild: true,
            children: true, childElementCount: true
        };

        window.cwProxy = function (root) {
            const realDoc = document;
            return new Proxy(realDoc, {
                get: function (target, prop) {
                    if (rootPropOverrides[prop]) {
                        if (prop === 'body' || prop === 'documentElement') return root;
                        if (prop === 'head') return null;
                        return root[prop];
                    }
                    if (rootMethods.indexOf(prop) !== -1 && typeof root[prop] === 'function') {
                        return root[prop].bind(root);
                    }
                    const value = target[prop];
                    return typeof value === 'function' ? value.bind(target) : value;
                },
                set: function (target, prop, value) {
                    target[prop] = value;
                    return true;
                },
                has: function (target, prop) {
                    return rootPropOverrides[prop] || rootMethods.indexOf(prop) !== -1 || (prop in target);
                }
            });
        };
    }

    // Browsers execute inline <script> tags inside declarative shadow DOM at parse time,
    // which is BEFORE this footer-loaded bundle has defined window.cwProxy. To work around
    // that, the PHP transformer tags wrapped scripts with type="application/cw-deferred".
    // Unknown script types are skipped by the parser. Here we find those deferred scripts,
    // re-create them as classic <script> elements (no type), set window.__cwCurrentRoot so
    // the IIFE body can find its shadow root, and replace them — which triggers execution.
    function rehydrateScripts(root) {
        const scripts = root.querySelectorAll('script[type="application/cw-deferred"]');
        for (let i = 0; i < scripts.length; i++) {
            const original = scripts[i];
            const fresh = document.createElement('script');
            for (let j = 0; j < original.attributes.length; j++) {
                const attr = original.attributes[j];
                if (attr.name.toLowerCase() === 'type') continue;
                fresh.setAttribute(attr.name, attr.value);
            }
            fresh.textContent = original.textContent;

            window.__cwCurrentRoot = root;
            try {
                original.replaceWith(fresh);
            } finally {
                window.__cwCurrentRoot = null;
            }
        }
    }

    // Custom element: <code-widget>
    // Browsers with declarative shadow DOM support already attached the shadowRoot
    // from <template shadowrootmode="open">. For older engines, attach it manually.
    if (typeof window.customElements !== 'undefined' && !window.customElements.get('code-widget')) {
        window.customElements.define('code-widget', class extends HTMLElement {
            connectedCallback() {
                let root = this.shadowRoot;

                if (!root) {
                    const tpl = this.querySelector('template[shadowrootmode], template[shadowroot]');
                    if (!tpl) return;

                    root = this.attachShadow({mode: 'open'});
                    root.appendChild(tpl.content.cloneNode(true));
                    tpl.remove();
                }

                rehydrateScripts(root);
            }
        });
    }

    // Iframe auto-resize: listens for postMessage from the iframe's child document and resizes the
    // matching iframe by data-cw-id.
    //
    // The sender is authenticated by IDENTITY, not by origin: we look up the iframe the message claims
    // to be about, then require that the message actually came from that element's contentWindow. This
    // is both necessary and stronger than checking event.origin.
    //   - Necessary: a seamless widget is sandboxed without allow-same-origin (so the sandbox actually
    //     contains something), which gives its document an opaque origin. event.origin is then "null"
    //     and an origin comparison against this page can never match.
    //   - Stronger: an origin check only proves the sender is same-origin, so any frame or window on
    //     the page could resize any widget by guessing its id. contentWindow identity cannot be forged.
    // Takes an already-validated positive integer id (see the listener below).
    function resolveFrame(id, source) {
        // querySelectorAll, not querySelector: the same widget can legitimately be embedded more than
        // once on a page, and each copy must resize itself. Matching on contentWindow picks the frame the
        // message actually came from, instead of always resolving to the first one and rejecting the rest.
        const frames = document.querySelectorAll('iframe[data-cw-id="' + id + '"]');
        for (let i = 0; i < frames.length; i++) {
            if (frames[i].contentWindow === source) return frames[i];
        }

        return null;
    }

    window.addEventListener('message', function (event) {
        // Anything on the page can postMessage to this window, so event.data is untrusted and is
        // validated by shape before anything is read out of it.
        //
        // This cannot throw either way: event.data always arrives as a structured clone, which carries
        // only plain data (functions and getters cannot survive it, so no hostile valueOf/toString can
        // run here), and reading .type off a primitive yields undefined rather than throwing. The checks
        // are here to reject wrong-shaped payloads explicitly instead of leaning on coercion to do it —
        // Number('900') and Number(true) both succeed, and neither is a message this handler should act on.
        const data = event.data;
        if (typeof data !== 'object' || data === null || Array.isArray(data)) return;
        if (data.type !== 'code-widget:resize') return;

        const height = typeof data.height === 'number' ? data.height : NaN;
        if (!Number.isFinite(height) || height <= 0) return;

        // The id goes into a CSS attribute selector, so it must be a real positive integer and nothing
        // else — that keeps the selector well-formed as well as being the shape the sender always uses.
        const id = data.id;
        if (typeof id !== 'number' || !Number.isInteger(id) || id <= 0) return;

        const iframe = resolveFrame(id, event.source);
        if (!iframe) return;

        iframe.style.height = Math.round(height) + 'px';
    });
})();
